import { Component, ComponentFactoryResolver, ComponentRef, Input, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NetworkConstants, PointOfInterest } from 'diwimamodelweb/lib';
import { deepCopy, diffObject } from 'diwimamodelweb/lib/utils/obejct-diff';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { InjectComponentDirective } from '../inject-component.directive';
import { PoiMapEditorData, PointOfInterestForm, PoiSubForm } from '../model/form';
import { PoiService } from '../poi.service';
import { UserFeedbackService } from '../user-feedback.service';
import { SettingsService } from '../settings.service';
import { ContextAccess, changedGeoFields, coordKey, isGeoLocked, isReadOnly, resolveContextAccess } from '../context-rights-picker/context-access';

const POI_GEO_FIELDS = ['huntingDistrictId', 'locationHeading', 'encodedContent', 'locationCoordinates']

// fix for unchanged geo on rights restriction
function poiGeoState(poi: any): Record<string, any> {
  return {
    huntingDistrictId:   poi?.huntingDistrictId ?? null,
    locationHeading:     poi?.locationHeading ?? null,
    encodedContent:      poi?.encodedContent ?? null,
    locationCoordinates: coordKey(poi?.locationCoordinates),
  }
}

@Component({
  selector: 'app-poi-form-handler',
  templateUrl: './poi-form-handler.component.html',
  styleUrls: ['./poi-form-handler.component.css']
})
export class PoiFormHandlerComponent implements OnInit {

  @Input() data: PointOfInterestForm
  @Input() formBuilder: FormBuilder
  @Input() userFeedbackService: UserFeedbackService
  @Input() translateService: TranslateService
  @Input() objectService: PoiService
  @Input() componentFactoryResolver: ComponentFactoryResolver
  @ViewChild(InjectComponentDirective, {static: true}) view: InjectComponentDirective;

  isCreate = false
  showBack = true
  currentlyActiveForm = PoiSubForm.BASE
  componentRef: ComponentRef<any>|undefined = undefined
  viewContainerRef: ViewContainerRef|undefined = undefined

  oldPoi: PointOfInterest
  contextAccess: ContextAccess = 'owner'

  constructor(private settingsService: SettingsService) { }

  ngOnInit(): void {
    if (!this.data.data) this.isCreate = true
    else {
      this.oldPoi = deepCopy(this.data.data, this.data.data.replacerFn)
    }

    const context = this.settingsService.selectedUserContext$?.value
    const subIds = this.settingsService.getSubcontextIds(); //?? (context as any)?.subContextIds

    this.contextAccess = resolveContextAccess(
      this.data.data as any,
      context?.identifier,
      subIds,
    )

    this.data.onSubmit = async () =>{
      if (!this.isCreate && this.rejectForbiddenChange()) return

      if (this.isCreate) {} else {
        try {
          this.userFeedbackService.loading.emit(true)
          const newPoi = deepCopy(this.data.data, this.data.data.replacerFn)
          ;(newPoi as any).notificationChannel = (this.data.data as any).notificationChannel
          const sbSuccessSave = this.translateService.instant('changes_saved')
          await this.objectService.updatePointOfInterest(this.oldPoi, newPoi)
          this.userFeedbackService.loading.emit(false)
          this.userFeedbackService.showSnackBar.emit(sbSuccessSave)
          this.data.onCancle()
        } catch (error) {
          console.error(error)
          this.userFeedbackService.loading.emit(false)
          this.userFeedbackService.showDialog.emit(
            {
              entryComponent: GenericDialogComponent,
              title: "Error",
              message: error?.error?.message ? error?.error?.message : JSON.stringify(error),
            }
          )
        }
      }
    }

    this.injectComponent(this.data.subFormRefs.get(this.currentlyActiveForm))
  }

  private rejectForbiddenChange(): boolean {
    if (isReadOnly(this.contextAccess)) {
      this.userFeedbackService.showSnackBar.emit('Dieses Objekt ist nur zur Ansicht freigegeben.')
      return true
    }
    if (isGeoLocked(this.contextAccess)) {
      const changed = changedGeoFields(
        poiGeoState(this.oldPoi), poiGeoState(this.data.data), POI_GEO_FIELDS,
      )
      if (changed.length) {
        this.userFeedbackService.showSnackBar.emit('Geodaten können nicht geändert werden.')
        return true
      }
    }
    return false
  }

  async navBack() {
    if (this.currentlyActiveForm == PoiSubForm.BASE) {
      const newPoi = deepCopy(this.data.data, this.data.data.replacerFn)
      const diff = diffObject(this.oldPoi, newPoi, {})
      const context = this.settingsService.selectedUserContext$?.value
      const rights = this.settingsService.rightsPercontext.get(context.identifier)

      if (Object.keys(diff).length == 0 || (Object.keys(diff).length == 1 && diff.shootRegions)) this.data.onCancle()
      else {
        // display dialog save changes?
        const title = this.translateService.instant('title_gr_changes')
        const message = this.translateService.instant('msg_poi_changes')
        const poiRights = rights[NetworkConstants.COLLECTION_POI]
        this.userFeedbackService.showDialog.emit(
          {
            entryComponent: GenericDialogComponent,
            title,
            message,
            cancleCallback: async () => {
              const sbChangesDismissed = this.translateService.instant('changes_dismissed')              
              this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
              this.data.onCancle()
            },
            positiveCallback: ()=> {
              if( poiRights['U'])
              this.data.onSubmit()
              else {
                const message = this.translateService.instant('not_allowed_update')
                this.userFeedbackService.showSnackBar.emit(message) 
              }
            }
          }
        )
      }
    } else {
      const form = this.data.subFormRefs.get(PoiSubForm.BASE)
      this.currentlyActiveForm = PoiSubForm.BASE
      this.injectComponent(form)
    }
  }

  injectComponent(componentRef: Type<any>) {
    this.viewContainerRef?.clear()
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentRef)
    this.viewContainerRef = this.view.viewContainerRef
    this.componentRef = this.viewContainerRef.createComponent(componentFactory)
    if (this.currentlyActiveForm == PoiSubForm.BASE) {
      this.componentRef.instance.data = this.data
      this.componentRef.instance.formBuilder = this.formBuilder
      this.componentRef.instance.userFeedbackService = this.userFeedbackService
      this.componentRef.instance.translateService = this.translateService
      this.componentRef.instance.showSubForm.subscribe((form: PoiSubForm) => {
        const subForm = this.data.subFormRefs.get(form)
        if (!subForm) return
        this.currentlyActiveForm = form
        this.injectComponent(subForm) 
      })

      // this.componentRef.instance.openMapEditor.subscribe((values: PoiMapEditorData) => {
      //  console.log(values)
      // const subForm = this.data.subFormRefs.get(form)
      // console.log(subForm)
      // if (!subForm) return
      // this.currentlyActiveForm = form
      // this.injectComponent(subForm) 
      // })
    } else if (this.currentlyActiveForm == PoiSubForm.LOCATION) {
      this.componentRef.instance.data = this.data
      this.componentRef.instance.fieldName = "locationCoordinates"
      this.componentRef.instance.submit.subscribe(() => {
        const form = this.data.subFormRefs.get(PoiSubForm.BASE)
        this.currentlyActiveForm = PoiSubForm.BASE
        this.injectComponent(form)
      })
    }
  }
}
